import React, { useContext, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import { getData } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import {
    Box,
    Card,
    CardContent,
    Fade,
    Grid,
    Paper,
    Skeleton,
    Typography,
    useTheme,
} from '@mui/material';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useNavigate } from 'react-router-dom';
import { ChartBar } from '../../components/chart-v2/bar';
import { ChartDoughnut } from '../../components/chart-v2/doughnut';
import { darkColors } from '../../components/chart-v2/colors';
import ListadoOperativoBanner from '../../components/listados/ListadoOperativoBanner';
import ProcessGuide from '../../components/orden/ProcessGuide';

const LIMITE_ITEMS_GRAFICO = 10;

function KpiCard({ icon: Icon, titulo, valor, subtitulo, color = 'primary', loading }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const colorMap = {
        primary: theme.palette.primary.main,
        success: theme.palette.success.main,
        info: theme.palette.info.main,
        warning: theme.palette.warning.main,
    };
    const bg = colorMap[color] || colorMap.primary;
    const bgLight = isDark ? `${bg}22` : `${bg}18`;

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                background: bgLight,
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: bg,
                            color: theme.palette.getContrastText(bg),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {Icon && <Icon sx={{ fontSize: 28 }} />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            gutterBottom
                            sx={{
                                color: "text.secondary",
                                fontWeight: 600
                            }}>
                            {titulo}
                        </Typography>
                        {loading ? (
                            <Skeleton variant="text" width={80} height={44} />
                        ) : (
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: "text.primary"
                                }}>
                                {valor}
                            </Typography>
                        )}
                        {subtitulo && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    mt: 0.5,
                                    display: 'block'
                                }}>
                                {subtitulo}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

function buildBarChartData(titulo, items, colorIndex = 0) {
    if (!items?.length) return null;
    const limitados = items.slice(0, LIMITE_ITEMS_GRAFICO);
    return {
        title: titulo,
        labels: limitados.map((i) => i.nombre),
        datasets: [
            {
                label: 'Cantidad',
                data: limitados.map((i) => i.cantidad),
                color: colorIndex,
            },
        ],
    };
}

function buildDoughnutChartData(titulo, items) {
    if (!items?.length) return null;
    const limitados = items.slice(0, LIMITE_ITEMS_GRAFICO);
    return {
        title: titulo,
        labels: limitados.map((i) => i.nombre),
        datasets: [
            {
                label: 'Cantidad',
                data: limitados.map((i) => i.cantidad),
                color: limitados.map((_, idx) => idx % darkColors.length),
            },
        ],
    };
}

function Page() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedChip, setSelectedChip] = useState('resumen');

    const isAdmin = !!(usuario?.es_admin || usuario?.nivel_usuario === 1);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            setError('');
            const response = await getData('dashboard/stats', usuario?.token);
            if (cancelled) return;
            if (response.error) {
                setError(response.error);
                setStats(null);
            } else {
                setStats(response.data || null);
            }
            setLoading(false);
        }
        if (usuario?.token) load();
        else setLoading(false);
        return () => { cancelled = true; };
    }, [usuario?.token]);

    const resumen = stats?.resumen || {};
    const operativas = resumen.operativas || {};
    const porTipoPractica = stats?.por_tipo_practica || [];
    const porEstablecimiento = stats?.por_establecimiento || [];
    const porLocalidadOrdenes = stats?.por_localidad_ordenes || [];
    const pacientesPorLocalidad = stats?.pacientes_por_localidad || [];

    const barTipoPractica = buildBarChartData('Órdenes por tipo de práctica', porTipoPractica, 4);
    const barEstablecimiento = buildBarChartData('Órdenes por establecimiento', porEstablecimiento, 6);
    const barLocalidadOrdenes = buildBarChartData('Órdenes por localidad (establecimiento)', porLocalidadOrdenes, 2);
    const doughnutPractica = buildDoughnutChartData('Distribución por tipo de práctica', porTipoPractica);
    const barPacientesLocalidad = buildBarChartData('Pacientes por localidad de residencia', pacientesPorLocalidad, 5);

    const variacion = resumen.variacion_porcentual_mes;
    const tendenciaSub = variacion != null
        ? (variacion >= 0 ? `+${variacion}% respecto al mes anterior` : `${variacion}% respecto al mes anterior`)
        : '';

    const chipItems = useMemo(() => {
        const items = [
            { key: 'resumen', label: 'Resumen', color: 'primary', count: resumen.total_ordenes ?? 0 },
            { key: 'para_responder', label: 'Para responder', color: 'warning', count: operativas.para_responder ?? 0 },
            { key: 'cerrar_turno', label: 'Cerrar turno', color: 'error', count: operativas.cerrar_turno ?? 0 },
        ];
        if (isAdmin) {
            items.push(
                { key: 'destrabar', label: 'Destrabar', color: 'error', count: operativas.pendiente_manual ?? 0 },
                { key: 'insumos', label: 'Insumos', color: 'warning', count: operativas.insumos_pendientes ?? 0 },
            );
        }
        return items;
    }, [resumen.total_ordenes, operativas, isAdmin]);

    const onChipChange = (key) => {
        setSelectedChip(key);
        if (key === 'resumen') return;
        navigate(`/orden-practica?chip=${key}`);
    };

    return (
        <Layout nombrePagina="Panel de inicio">
            <Fade in timeout={400}>
                <Box sx={{ pb: 3 }}>
                    <ListadoOperativoBanner
                        title="Panel de inicio"
                        description="Resumen de actividad y accesos rápidos a órdenes pendientes de respuesta, cierre o intervención."
                        icon={DashboardIcon}
                        accentColor="primary"
                        chips={chipItems}
                        selectedChip={selectedChip}
                        onChipChange={onChipChange}
                    />
                    <ProcessGuide
                        title="Guía rápida"
                        steps={[
                            'Las tarjetas muestran totales generales del sistema.',
                            'Los filtros superiores abren el hub de órdenes en el estado correspondiente.',
                            'Los gráficos desglosan por práctica, establecimiento y localidad.',
                        ]}
                    />

                    {error && (
                        <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                            <Typography>{error}</Typography>
                        </Paper>
                    )}

                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Números principales
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
                            <KpiCard
                                icon={AssignmentRoundedIcon}
                                titulo="Total de órdenes"
                                valor={resumen.total_ordenes ?? '—'}
                                loading={loading}
                                color="primary"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
                            <KpiCard
                                icon={PeopleRoundedIcon}
                                titulo="Pacientes atendidos"
                                valor={resumen.pacientes_atendidos ?? '—'}
                                subtitulo={`de ${resumen.total_pacientes ?? 0} pacientes registrados`}
                                loading={loading}
                                color="success"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
                            <KpiCard
                                icon={LocalHospitalRoundedIcon}
                                titulo="Establecimientos"
                                valor={resumen.total_establecimientos ?? '—'}
                                loading={loading}
                                color="info"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
                            <KpiCard
                                icon={CalendarMonthRoundedIcon}
                                titulo="Órdenes este mes"
                                valor={resumen.ordenes_mes_actual ?? '—'}
                                subtitulo={tendenciaSub}
                                loading={loading}
                                color="warning"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
                            <KpiCard
                                icon={TrendingUpRoundedIcon}
                                titulo="Variación mensual"
                                valor={variacion != null ? `${variacion > 0 ? '+' : ''}${variacion}%` : '—'}
                                subtitulo="respecto al mes anterior"
                                loading={loading}
                                color={variacion >= 0 ? 'success' : 'primary'}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Pendientes operativos
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                icon={AssignmentRoundedIcon}
                                titulo="Para responder"
                                valor={operativas.para_responder ?? '—'}
                                loading={loading}
                                color="warning"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                icon={CalendarMonthRoundedIcon}
                                titulo="Cierre de turno"
                                valor={operativas.cerrar_turno ?? '—'}
                                loading={loading}
                                color="error"
                            />
                        </Grid>
                        {isAdmin ? (
                            <>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <KpiCard
                                        icon={AssignmentRoundedIcon}
                                        titulo="Destrabar"
                                        valor={operativas.pendiente_manual ?? '—'}
                                        loading={loading}
                                        color="error"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <KpiCard
                                        icon={LocalHospitalRoundedIcon}
                                        titulo="Insumos pendientes"
                                        valor={operativas.insumos_pendientes ?? '—'}
                                        loading={loading}
                                        color="warning"
                                    />
                                </Grid>
                            </>
                        ) : null}
                    </Grid>

                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Por tipo de práctica
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {loading ? (
                            <Grid size={12}>
                                <Skeleton variant="rounded" height={320} />
                            </Grid>
                        ) : (
                            <>
                                {barTipoPractica && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                            <ChartBar data={barTipoPractica} />
                                        </Paper>
                                    </Grid>
                                )}
                                {doughnutPractica && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                            <ChartDoughnut data={doughnutPractica} />
                                        </Paper>
                                    </Grid>
                                )}
                                {!barTipoPractica && !doughnutPractica && (
                                    <Grid size={12}>
                                        <Typography sx={{ color: 'text.secondary' }}>No hay datos de prácticas.</Typography>
                                    </Grid>
                                )}
                            </>
                        )}
                    </Grid>

                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Por establecimiento
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {loading ? (
                            <Grid size={12}>
                                <Skeleton variant="rounded" height={320} />
                            </Grid>
                        ) : barEstablecimiento ? (
                            <Grid size={12}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <ChartBar data={barEstablecimiento} />
                                </Paper>
                            </Grid>
                        ) : (
                            <Grid size={12}>
                                <Typography sx={{ color: 'text.secondary' }}>No hay datos de establecimientos.</Typography>
                            </Grid>
                        )}
                    </Grid>

                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Por localidad
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {loading ? (
                            <Grid size={12}>
                                <Skeleton variant="rounded" height={320} />
                            </Grid>
                        ) : (
                            <>
                                {barLocalidadOrdenes && (
                                    <Grid size={{ xs: 12, lg: 6 }}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                            <ChartBar data={barLocalidadOrdenes} />
                                        </Paper>
                                    </Grid>
                                )}
                                {barPacientesLocalidad && (
                                    <Grid size={{ xs: 12, lg: 6 }}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                            <ChartBar data={barPacientesLocalidad} />
                                        </Paper>
                                    </Grid>
                                )}
                                {!barLocalidadOrdenes && !barPacientesLocalidad && (
                                    <Grid size={12}>
                                        <Typography sx={{ color: 'text.secondary' }}>No hay datos por localidad.</Typography>
                                    </Grid>
                                )}
                            </>
                        )}
                    </Grid>
                </Box>
            </Fade>
        </Layout>
    );
}

export default Page;
